import { useEffect, useState } from "react";
import { get } from "../lib/api";
import { capture } from "../lib/analytics";
import { Header, Spinner, Muted, GildedFrame, Empty, ErrorText } from "../components/ui";

// Order support has to cover people who never keep the app. A web-origin order
// is the same record in the same pipeline and shows in the operator's Orders tab
// the same way — the only thing missing was somewhere for the buyer to look.

interface Order {
  id: string;
  status: string;
  createdAt?: number;
  label?: string;
  trackingUrl?: string;
  items?: { label: string; qty: number }[];
  pricing?: { totalCents: number; currency: string };
}

const HUMAN: Record<string, string> = {
  pending_payment: "Awaiting payment",
  paid: "Paid, preparing",
  fulfilling: "Preparing",
  submitted: "Sent to the printer",
  in_production: "Being printed",
  shipped: "Shipped",
  delivered: "Delivered",
  canceled: "Canceled",
  refunded: "Refunded",
};

export function Orders() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await get<{ orders: Order[] }>("/v1/print/orders");
        setOrders(res.orders ?? []);
        capture("webOrdersViewed");
      } catch {
        setErr("Couldn't load your orders. Please refresh.");
      }
    })();
  }, []);

  if (err) return <ErrorText>{err}</ErrorText>;
  if (!orders) return <Spinner />;

  if (orders.length === 0) {
    return (
      <>
        <Header kicker="Orders" title="Your orders" />
        <Empty title="No orders yet" body="Prints you order will show up here, with tracking as soon as they ship." />
      </>
    );
  }

  return (
    <>
      <Header kicker="Orders" title="Your orders" />
      <div className="space-y-3">
        {orders.map((o) => (
          <GildedFrame key={o.id}>
            <div className="p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-serif text-[15px] text-ivory">
                  {o.items?.map((i) => `${i.label}${i.qty > 1 ? ` ×${i.qty}` : ""}`).join(", ") || o.label || "Order"}
                </div>
                <div className="rounded-full bg-gold/10 px-2.5 py-0.5 text-[11px] text-gold">
                  {HUMAN[o.status] || o.status}
                </div>
              </div>
              <Muted className="mt-1 font-mono text-[11px]">#{o.id.slice(0, 12)}</Muted>
              {o.pricing && (
                <Muted className="mt-1">
                  ${(o.pricing.totalCents / 100).toFixed(2)} {o.pricing.currency}
                </Muted>
              )}
              {o.trackingUrl && (
                <a
                  href={o.trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-[13px] text-gold hover:underline"
                >
                  Track this parcel →
                </a>
              )}
            </div>
          </GildedFrame>
        ))}
      </div>
      <Muted className="mt-6 text-[12px]">
        Need help with an order? Email support@holyshot.cc with your order number and we'll pick it up, whether or not you
        have the app.
      </Muted>
    </>
  );
}
